import { numberAttribute } from '@angular/core';

/**
 * Adjust the height of a textarea to fit its content without exceeding a maximum height.
 *
 * This function is useful for auto-growing textareas while typing.
 * It resets the textarea height to "auto" first, calculates the new height based on `scrollHeight`,
 * caps it at `maxHeight`, applies it to the textarea, and returns the new height.
 *
 * @param event - The input/textarea event containing the target HTMLTextAreaElement
 * @param maxHeight - Maximum height in pixels (default: 120)
 * @returns The new height in pixels applied to the textarea
 *
 * @example
 * <textarea (onInput)="updateTargetHeight($event)"></textarea>
 *
 * // Programmatically:
 * const newHeight = updateTargetHeight({ target: myTextarea } as Event, 150);
 */
export function updateTargetHeight(event: Event, maxHeight = 120): number {
  const target = event.target as HTMLTextAreaElement;
  target.style.height = 'auto'; // reset
  const newHeight = Math.min(target.scrollHeight, maxHeight);
  target.style.height = `${newHeight}px`;
  return newHeight;
}

const hhmmssRegex = new RegExp(/\d{2}:\d{2}:\d{2}/);
/**
 * Convert a time string in HH:mm:ss format to a duration in seconds.
 * Returns 0 if the input is invalid or not in the correct format.
 *
 * @param timeStr - Time string in the format "HH:mm:ss"
 * @returns Duration in seconds
 */
export function timeToDuration(timeStr: string): number {
  if (!timeStr || !hhmmssRegex.test(timeStr)) {
    return 0;
  }

  const [hours, minutes, seconds] = timeStr.split(':').map((time) => numberAttribute(time, 0));
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Convert a duration in seconds to a formatted time string (HH:mm:ss).
 *
 * @param duration - Duration in seconds
 * @param removeLeadingHoursZero - If true, removes the leading '00' hour.
 *   When hours are removed, minutes are not zero-padded.
 * @returns Time string formatted as "HH:mm:ss" (or "m:ss" if leading hours are removed)
 */
export function durationToTime(duration: number, removeLeadingHoursZero = false): string {
  if (isNaN(duration)) {
    return '';
  }

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  const parts = [
    numberToString(hours), // HH
    numberToString(minutes), // mm
    numberToString(seconds), // ss (always two digits)
  ];
  if (removeLeadingHoursZero) {
    // delete hours if 00
    if (parts[0] === '00') {
      parts.shift();

      // if hours were deleted, we don't want pad on minutes
      parts[0] = String(minutes);
    } else {
      // if hours remains, we don't want pad on hours
      parts[0] = String(hours);
    }
  }

  return parts.join(':');
}

/**
 * Format a time string to a compact representation by removing leading zeros and milliseconds.
 *
 * Removes milliseconds (if present) and leading hour zeros. When hours are removed (00:mm:ss),
 * minutes are not zero-padded. When hours remain, they are not zero-padded but minutes and seconds are.
 *
 * @param timeStr - Time string in format "HH:mm:ss" or "HH:mm:ss.SSS"
 * @returns Compact time string without leading zeros
 *
 * @example
 * formatCompactTime('00:05.116')  // "0:05"
 * formatCompactTime('00:00:05.116')  // "0:05"
 * formatCompactTime('00:00:50.116')  // "0:50"
 * formatCompactTime('00:04:00.000')  // "4:00"
 * formatCompactTime('00:12:36.500')  // "12:36"
 * formatCompactTime('01:02:00')      // "1:02:00"
 * formatCompactTime('03:10:04')      // "3:10:04"
 */
export function formatCompactTime(timeStr: string): string {
  if (!timeStr) {
    return '';
  }

  // Remove milliseconds if present (everything after '.')
  const withoutMilliseconds = timeStr.split('.')[0];

  // Split into time parts
  const parts = withoutMilliseconds.split(':');

  // Handle mm:ss format (2 parts)
  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return `${parseInt(minutes, 10)}:${seconds}`;
  }

  // Handle HH:mm:ss format (3 parts)
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;

    // If hours are '00', remove them and don't pad minutes
    if (hours === '00') {
      return `${parseInt(minutes, 10)}:${seconds}`;
    }

    // Otherwise, remove leading zero from hours, keep padding on minutes/seconds
    return `${parseInt(hours, 10)}:${minutes}:${seconds}`;
  }

  // Invalid format - return empty string
  return '';
}

/**
 * Convert a number to a string, always at least two characters long,
 * padding with a leading zero if necessary.
 *
 * Commonly used to format hours, minutes, or seconds for time strings.
 *
 * @param value - The number to convert
 * @returns A string representation of the number, padded with a leading zero if less than 10
 *
 * @example
 * numberToString(0)   // "00"
 * numberToString(5)   // "05"
 * numberToString(12)  // "12"
 */
function numberToString(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Validates if a file matches the accept criteria.
 *
 * Supports the same format as the HTML input accept attribute:
 * - MIME type wildcards (e.g., 'image/*', 'audio/*')
 * - Exact MIME types (e.g., 'application/pdf', 'image/png')
 * - File extensions with dot (e.g., '.pdf', '.jpg')
 * - File extensions without dot (e.g., 'pdf', 'jpg')
 * - Mixed formats (e.g., 'image/*,.pdf,application/json')
 *
 * @param file - The file to validate
 * @param accept - Accept string in the same format as HTML input accept attribute
 * @returns true if file is valid, false otherwise
 *
 * @example
 * isFileTypeValid(file, 'image/*')              // accepts all images
 * isFileTypeValid(file, '.pdf,.doc')            // accepts PDF and DOC files
 * isFileTypeValid(file, 'image/*,.pdf')         // accepts all images and PDFs
 * isFileTypeValid(file, 'application/pdf')      // accepts only PDF MIME type
 */
export function isFileTypeValid(file: File, accept: string): boolean {
  if (!accept || accept.trim() === '') {
    return true; // No restrictions
  }

  const acceptParts = accept.split(',').map((part) => part.trim().toLowerCase());
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return acceptParts.some((acceptPart) => {
    // Handle MIME type wildcards (e.g., 'image/*', 'audio/*')
    if (acceptPart.includes('/*')) {
      const mimeCategory = acceptPart.split('/')[0];
      return fileType.startsWith(mimeCategory + '/');
    }

    // Handle exact MIME types (e.g., 'application/pdf', 'image/png')
    if (acceptPart.includes('/')) {
      return fileType === acceptPart;
    }

    // Handle file extensions (e.g., '.pdf', '.jpg')
    if (acceptPart.startsWith('.')) {
      return fileName.endsWith(acceptPart);
    }

    // Handle extensions without dot (e.g., 'pdf', 'jpg')
    return fileName.endsWith('.' + acceptPart);
  });
}

/**
 * Convert a file size in bytes into a human-readable string with appropriate units.
 *
 * Uses binary units (1 KB = 1024 bytes) and supports Bytes, KB, MB, GB, TB.
 * Returns an empty string for null, undefined, or NaN values.
 *
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places to display (default: 2)
 * @returns Formatted file size string with units (e.g., "1.5 MB", "0 Bytes")
 *
 * @example
 * formatFileSize(0)           // "0 Bytes"
 * formatFileSize(1024)        // "1 KB"
 * formatFileSize(1536, 2)     // "1.5 KB"
 * formatFileSize(1048576)     // "1 MB"
 * formatFileSize(null)        // ""
 */
export function formatFileSize(bytes: number | null | undefined, decimals = 2): string {
  if (bytes == null || isNaN(bytes)) {
    return '';
  }

  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
